import {computed, inject, Injectable, signal} from '@angular/core';
import {
  AuditInfo,
  PlannedAction,
  ProjectOfficeResponse,
  SystemRecordingEntryRequest,
  SystemRecordingsControllerService,
  SystemRecordingToolControllerService,
  SystemRecordingToolsUpsertRequest,
  SystemRecordingUpsertRequest,
  SystemRecordingUpsertResponse,
} from '@frontend/shared-api';
import {
  AutoCompleteGroup,
  FunctionEntry,
  ModuleData,
  PlannedActionOption,
  ProviderData,
  ToolData,
} from './system-recording.types';
import {FunctionEntryService} from './services/function-entry.service';
import {LoadOrSyncSystemRecordingDataService} from './services/load-or-sync-system-recording-data.service';
import {firstValueFrom} from 'rxjs';
import {TranslocoService} from '@jsverse/transloco';
import {ProjectOfficesProviderService} from './services/project-offices-provider.service';
import {ToastService} from '@frontend/toast';
import {latestAuditInfo} from '../../../../../../shared/utils/audit-info.utils';

@Injectable({providedIn: 'root'})
export class SystemRecordingStateService {
  private readonly systemRecordingsController = inject(SystemRecordingsControllerService);
  private readonly systemRecordingToolController = inject(SystemRecordingToolControllerService);
  private readonly functionEntryService = inject(FunctionEntryService);
  private readonly loadOrSyncService = inject(LoadOrSyncSystemRecordingDataService);
  private readonly translocoService = inject(TranslocoService);
  private readonly projectOfficesProviderService = inject(ProjectOfficesProviderService);
  private readonly toastService = inject(ToastService);

  private projectId!: number;

  readonly moduleData = signal<ModuleData[]>([]);
  readonly availableProviders = signal<ProviderData[]>([]);

  readonly loading = signal(false);
  readonly dataSavedTrigger = signal(0);

  readonly hrAuditInfo = signal<AuditInfo | null>(null);
  readonly nonHrAuditInfo = signal<AuditInfo | null>(null);

  readonly auditInfo = computed(() => latestAuditInfo([this.hrAuditInfo(), this.nonHrAuditInfo()]));

  private readonly moduleSnapshots = signal<Map<number, string>>(new Map());

  readonly filteredProviders = signal<AutoCompleteGroup[]>([]);
  readonly filteredTools = signal<AutoCompleteGroup[]>([]);

  readonly providerLookupMap = computed(() => {
    const map = new Map<number, ProviderData>();
    this.availableProviders().forEach((provider) => {
      map.set(provider.providerId, provider);
    });
    return map;
  });

  readonly toolLookupMap = computed(() => {
    const map = new Map<number, Map<number, ToolData>>();
    this.availableProviders().forEach((provider) => {
      const toolMap = new Map<number, ToolData>();
      provider.tools.forEach((tool) => {
        toolMap.set(tool.toolId, tool);
      });
      map.set(provider.providerId, toolMap);
    });
    return map;
  });

  async initialize(projectId: number): Promise<void> {
    this.projectId = projectId;

    const [hrToolsFromDirectus, offices] = await Promise.all([
      this.loadOrSyncService.loadHrToolsFromDirectus(projectId),
      this.projectOfficesProviderService.getOffices(projectId),
    ]);

    let providerData = await this.loadOrSyncService.loadProviderAndToolsFromBackend(projectId);
    if (providerData.length <= 0) {
      await this.loadOrSyncService.seedProviderAndToolsAtBackend(projectId, hrToolsFromDirectus);
      providerData = await this.loadOrSyncService.loadProviderAndToolsFromBackend(projectId);
    }

    providerData = this.translateProviderData(providerData, hrToolsFromDirectus);

    const {modules, auditInfo} = await this.loadOrSyncService.loadFixedModulesAndFunctions(
      projectId,
      offices,
    );

    this.availableProviders.set(providerData);
    this.moduleData.set(modules);
    this.hrAuditInfo.set(auditInfo);

    modules.forEach((module) => this.captureModuleSnapshot(module.id));
  }

  updateFunctionEntry(uniqueId: string, updates: Partial<FunctionEntry>): void {
    const currentData = this.moduleData();
    const updatedModules = this.functionEntryService.updateFunctionEntry(
      currentData,
      uniqueId,
      updates,
    );

    this.moduleData.set(updatedModules);
  }

  onPlannedActionChange(uniqueId: string, plannedAction: PlannedAction): void {
    const updates = this.functionEntryService.handlePlannedActionChange(plannedAction);
    this.updateFunctionEntry(uniqueId, updates);
  }

  onProviderChange(uniqueId: string, providerId: number): void {
    const updates = this.functionEntryService.handleProviderChange(providerId);
    this.updateFunctionEntry(uniqueId, updates);
  }

  onToolChange(uniqueId: string, toolId: number, providerId: number): void {
    this.updateFunctionEntry(uniqueId, {
      toolId,
      providerId,
    });
  }

  onOfficeSelectionChange(uniqueId: string, selectedOffices: ProjectOfficeResponse[]): void {
    this.updateFunctionEntry(uniqueId, {selectedOffices});
  }

  private isCustomProvider(provider: ProviderData): boolean {
    return provider.tools.every((tool) => !tool.directusId);
  }

  searchProviders(query: string): void {
    const filteredProviders = this.availableProviders().filter((provider) =>
      provider.providerName.toLowerCase().includes(query.toLowerCase()),
    );

    this.filteredProviders.set(
      this.buildAutoCompleteGroups(
        filteredProviders,
        (provider) => this.isCustomProvider(provider),
        'Custom Providers',
        'Available Providers',
      ),
    );
  }

  searchTools(query: string, currentProviderId: number | null): void {
    if (!currentProviderId) {
      this.filteredTools.set([]);
      return;
    }

    const provider = this.availableProviders().find((p) => p.providerId === currentProviderId);

    if (!provider) {
      this.filteredTools.set([]);
      return;
    }

    const filteredTools = provider.tools.filter((tool) =>
      tool.toolName.toLowerCase().includes(query.toLowerCase()),
    );

    this.filteredTools.set(
      this.buildAutoCompleteGroups(
        filteredTools,
        (tool) => !tool.directusId,
        'Custom Tools',
        'Available Tools',
      ),
    );
  }

  private buildAutoCompleteGroups<T>(
    items: T[],
    isCustom: (item: T) => boolean,
    customLabel: string,
    standardLabel: string,
  ): AutoCompleteGroup[] {
    const customItems = items.filter((item) => isCustom(item));
    const standardItems = items.filter((item) => !isCustom(item));

    const groups: AutoCompleteGroup[] = [];

    if (customItems.length > 0) {
      groups.push({label: customLabel, items: customItems});
    }

    if (standardItems.length > 0) {
      groups.push({label: standardLabel, items: standardItems});
    }

    return groups;
  }

  async createNewProvider(
    providerName: string,
  ): Promise<(ProviderData & {wasCreated: boolean}) | null> {
    const trimmed = providerName.trim();
    if (!trimmed) return null;

    const existing = this.availableProviders().find(
      (p) => p.providerName.toLowerCase() === trimmed.toLowerCase(),
    );
    if (existing) return {...existing, wasCreated: false};

    const request: SystemRecordingToolsUpsertRequest = {
      providers: [{providerName: trimmed, tools: []}],
    };

    await firstValueFrom(
      this.systemRecordingToolController.upsertSystemRecordingTools({
        projectId: this.projectId,
        systemRecordingToolsUpsertRequest: request,
      }),
    );

    const updated = await this.loadOrSyncService.loadProviderAndToolsFromBackend(this.projectId);
    this.availableProviders.set(updated);

    const newProvider =
      updated.find((p) => p.providerName.toLowerCase() === trimmed.toLowerCase()) ?? null;
    if (newProvider) return {...newProvider, wasCreated: true};
    return null;
  }

  async createNewTool(toolName: string, providerId: number): Promise<ToolData | null> {
    const trimmed = toolName.trim();
    if (!trimmed) return null;

    const provider = this.availableProviders().find((p) => p.providerId === providerId);
    if (!provider) return null;

    const existing = provider.tools.find((t) => t.toolName.toLowerCase() === trimmed.toLowerCase());
    if (existing) return existing;

    const request: SystemRecordingToolsUpsertRequest = {
      providers: [{providerName: provider.providerName, tools: [{toolName: trimmed}]}],
    };

    await firstValueFrom(
      this.systemRecordingToolController.upsertSystemRecordingTools({
        projectId: this.projectId,
        systemRecordingToolsUpsertRequest: request,
      }),
    );

    const updated = await this.loadOrSyncService.loadProviderAndToolsFromBackend(this.projectId);
    this.availableProviders.set(updated);

    const updatedProvider = updated.find((p) => p.providerId === providerId);
    return (
      updatedProvider?.tools.find((t) => t.toolName.toLowerCase() === trimmed.toLowerCase()) ?? null
    );
  }

  async deleteProvider(providerId: number): Promise<boolean> {
    const provider = this.availableProviders().find((p) => p.providerId === providerId);
    if (!provider) return false;

    const isCustom = provider.tools.every((t) => !t.directusId);
    if (!isCustom) {
      return false;
    }

    await firstValueFrom(
      this.systemRecordingToolController.deleteProvider({
        projectId: this.projectId,
        providerId: providerId,
      }),
    );

    const updated = await this.loadOrSyncService.loadProviderAndToolsFromBackend(this.projectId);
    this.availableProviders.set(updated);

    return true;
  }

  async deleteTool(toolId: number, providerId: number): Promise<boolean> {
    const provider = this.availableProviders().find((p) => p.providerId === providerId);
    if (!provider) return false;

    const tool = provider.tools.find((t) => t.toolId === toolId);
    if (!tool) return false;

    if (tool.directusId) {
      return false;
    }

    await firstValueFrom(
      this.systemRecordingToolController.deleteTool({
        projectId: this.projectId,
        toolId: toolId,
      }),
    );

    const updated = await this.loadOrSyncService.loadProviderAndToolsFromBackend(this.projectId);
    this.availableProviders.set(updated);

    return true;
  }

  duplicateFunction(moduleId: number, currentUniqueId: string): void {
    const currentData = this.moduleData();
    const updatedData = this.functionEntryService.duplicateFunctionInstance(
      currentData,
      moduleId,
      currentUniqueId,
    );

    this.moduleData.set(updatedData);
  }

  removeFunction(moduleId: number, entryUniqueId: string): void {
    const currentData = this.moduleData();

    const updatedData = this.functionEntryService.removeFunctionInstance(
      currentData,
      moduleId,
      entryUniqueId,
    );

    this.moduleData.set(updatedData);
  }

  async deleteFunctionInstance(moduleId: number, backendId: number): Promise<void> {
    await firstValueFrom(
      this.systemRecordingsController.deleteSystemRecording({
        projectId: this.projectId,
        id: backendId,
      }),
    );

    const currentData = this.moduleData();
    const updatedData = currentData.map((module) => {
      if (module.id !== moduleId) {
        return module;
      }

      const updatedFunctions = module.functions.filter((func) => func.backendId !== backendId);

      return {
        ...module,
        functions: updatedFunctions,
        hasInScopeFunctions: updatedFunctions.some((func) => func.isInProjectScope),
      };
    });

    this.moduleData.set(updatedData);
    this.captureModuleSnapshot(moduleId);

    await this.toastService.showSuccess(
      'common.success',
      'project.steps.systemRecording.functionDeleted',
    );
  }

  getPlannedActionOptions(): PlannedActionOption[] {
    return [
      {
        label: this.translocoService.translate('project.steps.systemRecording.plannedAction.new'),
        value: PlannedAction.NEW,
      },
      {
        label: this.translocoService.translate(
          'project.steps.systemRecording.plannedAction.replace',
        ),
        value: PlannedAction.REPLACE,
      },
      {
        label: this.translocoService.translate(
          'project.steps.systemRecording.plannedAction.retain',
        ),
        value: PlannedAction.RETAIN,
      },
      {
        label: this.translocoService.translate(
          'project.steps.systemRecording.plannedAction.notRelevant',
        ),
        value: PlannedAction.NOT_RELEVANT,
      },
      {
        label: this.translocoService.translate(
          'project.steps.systemRecording.plannedAction.noSystem',
        ),
        value: PlannedAction.NO_SYSTEM,
      },
    ];
  }

  getPlannedInProjectScopeActionOptions(): PlannedActionOption[] {
    return [
      {
        label: this.translocoService.translate('project.steps.systemRecording.plannedAction.new'),
        value: PlannedAction.NEW,
      },
      {
        label: this.translocoService.translate(
          'project.steps.systemRecording.plannedAction.replace',
        ),
        value: PlannedAction.REPLACE,
      },
    ];
  }

  async saveModule(projectId: number, moduleId: number): Promise<void> {
    const module = this.moduleData().find((m) => m.id === moduleId);
    if (!module) {
      return;
    }

    const completedFunctions = this.getCompletedFunctions(module);

    const request = this.buildSaveRequest(completedFunctions);

    const saveResponse = await firstValueFrom(
      this.systemRecordingsController.upsertSystemRecording({
        projectId,
        moduleDirectusId: String(moduleId),
        systemRecordingUpsertRequest: request,
      }),
    );

    this.updateModuleFromSaveResponse(moduleId, saveResponse);

    this.captureModuleSnapshot(moduleId);

    this.dataSavedTrigger.update((v) => v + 1);
  }

  private getCompletedFunctions(module: ModuleData): FunctionEntry[] {
    return module.functions.filter((func) => this.functionEntryService.isFunctionComplete(func));
  }

  private updateModuleFromSaveResponse(
    moduleId: number,
    saveResponse: SystemRecordingUpsertResponse,
  ): void {
    if (!saveResponse?.entries) {
      return;
    }

    const currentData = this.moduleData();

    const updatedData = currentData.map((module) => {
      if (module.id !== moduleId) {
        return module;
      }

      const updatedFunctions = module.functions.map((func) => {
        const responseEntry = func.backendId
          ? saveResponse.entries.find((entry) => entry.id === func.backendId)
          : saveResponse.entries.find(
              (entry) =>
                Number(entry.functionDirectusId) === func.directusId &&
                !module.functions.some((f) => f.backendId === entry.id),
            );

        if (responseEntry && func.backendId !== responseEntry.id) {
          return {...func, backendId: responseEntry.id};
        }

        return func;
      });

      return {...module, functions: updatedFunctions};
    });

    this.moduleData.set(updatedData);
  }

  private buildSaveRequest(completedFunctions: FunctionEntry[]): SystemRecordingUpsertRequest {
    const entries: SystemRecordingEntryRequest[] = completedFunctions.map((func) => {
      const entry: SystemRecordingEntryRequest = {
        id: func.backendId ?? undefined,
        functionDirectusId: String(func.directusId),
        plannedAction: func.plannedAction ?? PlannedAction.NEW,
        officeIds: func.selectedOffices.map((office) => office.officeId),
        toolId: func.toolId ?? undefined,
      };

      return entry;
    });

    return {entries};
  }

  private translateProviderData(
    providerData: ProviderData[],
    hrToolsFromDirectus: ProviderData[],
  ): ProviderData[] {
    const directusToolMap = new Map<string, string>();
    hrToolsFromDirectus.forEach((provider) => {
      provider.tools.forEach((tool) => {
        if (tool.directusId) {
          directusToolMap.set(tool.directusId, tool.toolName);
        }
      });
    });

    return providerData.map((provider) => ({
      ...provider,
      tools: provider.tools.map((tool) => {
        if (tool.directusId && directusToolMap.has(tool.directusId)) {
          const translatedName = directusToolMap.get(tool.directusId);
          return {
            ...tool,
            toolName: translatedName ?? tool.toolName,
          };
        }
        return tool;
      }),
    }));
  }

  captureModuleSnapshot(moduleId: number): void {
    const module = this.moduleData().find((m) => m.id === moduleId);
    if (!module) return;

    const snapshot = JSON.stringify(module.functions);
    const currentSnapshots = this.moduleSnapshots();
    const updatedSnapshots = new Map(currentSnapshots);
    updatedSnapshots.set(moduleId, snapshot);
    this.moduleSnapshots.set(updatedSnapshots);
  }

  hasModuleChanges(moduleId: number): boolean {
    const module = this.moduleData().find((m) => m.id === moduleId);
    if (!module) return false;

    const snapshot = this.moduleSnapshots().get(moduleId);
    if (!snapshot) return false;

    const current = JSON.stringify(module.functions);
    return current !== snapshot;
  }
}
